import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Wallet, Copy, ExternalLink, Loader2, Sparkles, ArrowDownToLine, DollarSign, AlertCircle, QrCode, Download, FileText } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import ShareKitPanel from '../components/ShareKitPanel';
import PromoterPageCard from '../components/PromoterPageCard';
import PartnerLinkTools from '../components/PartnerLinkTools';
import PromoterLinkGuide from '../components/PromoterLinkGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StreetTeamDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [params] = useSearchParams();

  const [me, setMe] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [txs, setTxs] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [campTab, setCampTab] = useState('current');
  const [qrOpen, setQrOpen] = useState(null); // event_id of expanded QR

  const downloadQr = (eventId, eventName) => {
    const canvas = document.getElementById(`qr-canvas-${eventId}`);
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(eventName || 'event').replace(/[^a-z0-9]/gi, '_').toLowerCase()}-tracking-qr.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success('QR downloaded');
  };

  // Force-download marketing image even when hosted on a different origin —
  // a plain `<a download>` is ignored cross-origin, so we fetch as blob first.
  const downloadImage = async (url, eventName, idx) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${(eventName || 'event').replace(/[^a-z0-9]/gi, '_').toLowerCase()}-asset-${idx + 1}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      toast.success('Image downloaded');
    } catch (err) {
      // Fall back to opening in a new tab so the user can right-click → save
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.message('Opening image in new tab — right-click to save');
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    // Fetch /me FIRST and independently so a transient failure on either of
    // the secondary endpoints can't bounce an enrolled user back to the
    // "Join now" screen (the entire-Promise.all bug that caused the
    // re-signup loop).
    try {
      const meRes = await axios.get(`${API}/street-team/me`, { headers });
      setMe(meRes.data);
    } catch (e) {
      setMe(null);
      setLoading(false);
      return;
    }
    // Secondary data — best effort, never blocks the page.
    const [cRes, tRes, sRes] = await Promise.allSettled([
      axios.get(`${API}/promo/me/campaigns`, { headers }),
      axios.get(`${API}/street-team/wallet/transactions?limit=20`, { headers }),
      axios.get(`${API}/street-team/statements`, { headers }),
    ]);
    if (cRes.status === 'fulfilled') setCampaigns(cRes.value.data.campaigns || []);
    if (tRes.status === 'fulfilled') setTxs(tRes.value.data.transactions || []);
    if (sRes.status === 'fulfilled') setStatements(sRes.value.data.statements || []);
    setLoading(false);
  };

  const downloadStatement = async (eventId, name) => {
    try {
      const r = await axios.get(`${API}/street-team/statements/${eventId}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eventism-statement-${(name || eventId).replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch { toast.error('Could not download statement'); }
  };

  useEffect(() => {
    if (!token) { navigate('/login?returnTo=/dashboard/street-team'); return; }
    fetchAll();
    if (params.get('stripe') === 'return') {
      // refresh status when user returns from onboarding
      axios.post(`${API}/street-team/stripe/refresh-status`, {}, { headers }).then(() => fetchAll()).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [token]);

  const optIn = async () => {
    try { await axios.post(`${API}/street-team/opt-in`, {}, { headers }); toast.success('Welcome!'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const onboardStripe = async () => {
    setWorking(true);
    try {
      const res = await axios.post(`${API}/street-team/stripe/onboard`, {}, { headers });
      window.location.href = res.data.onboarding_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Stripe error'); setWorking(false); }
  };

  const refreshStripe = async () => {
    try { const res = await axios.post(`${API}/street-team/stripe/refresh-status`, {}, { headers }); toast.success(res.data.payouts_enabled ? 'Payouts enabled!' : 'Onboarding incomplete'); fetchAll(); }
    catch (e) { toast.error('Failed'); }
  };

  const requestPayout = async (amountCents) => {
    if (!amountCents || amountCents <= 0) return;
    try {
      const res = await axios.post(`${API}/street-team/payout`, { amount_cents: amountCents }, { headers });
      toast.success(res.data.instant ? 'Instant payout sent!' : 'Standard payout queued');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copy = (text, label = 'Link') => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  if (!me?.enabled) {
    return (
      <div className="min-h-screen bg-background pt-20" data-testid="street-team-not-enabled">
        <div className="max-w-md mx-auto p-6 text-center">
          <Sparkles className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold mb-2">Join the Eventism Street Team</h1>
          <p className="text-sm text-muted-foreground mb-5">Earn commission promoting events you love.</p>
          <Button onClick={optIn} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="opt-in-btn">Join now</Button>
          <p className="text-xs mt-4"><Link to="/promo-partners" className="text-indigo-400 hover:underline">Browse open campaigns →</Link></p>
        </div>
      </div>
    );
  }

  if (me?.banned) {
    return (
      <div className="min-h-screen pt-20 max-w-md mx-auto p-6 text-center">
        <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
        <h1 className="text-xl font-bold">Your street-team status is suspended</h1>
        <p className="text-sm text-muted-foreground mt-2">Contact support if you think this is a mistake.</p>
      </div>
    );
  }

  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const todayStr = new Date().toISOString().slice(0, 10);
  const isPastCampaign = (c) => !!(c.event?.event_date) && c.event.event_date < todayStr;
  const currentCampaigns = campaigns.filter(c => !isPastCampaign(c));
  const pastCampaigns = campaigns.filter(isPastCampaign);
  const visibleCampaigns = campTab === 'past' ? pastCampaigns : currentCampaigns;
  const w = me.wallet || {};
  const stripeReady = w.stripe_payouts_enabled;
  const balance = w.balance_cents || 0;
  const pending = w.pending_cents || 0;

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="street-team-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold">Street Team Dashboard</h1>
          <Link to="/promo-partners"><Button variant="outline" size="sm">Browse campaigns →</Button></Link>
        </div>

        {/* Promoter public page */}
        <PromoterPageCard token={token} />

        {/* How links work — education hub */}
        <PromoterLinkGuide token={token} />

        {/* Wallet */}
        <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-indigo-500/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Available to cash out</p>
                <p className="text-4xl font-bold mt-1 text-emerald-400" data-testid="wallet-balance">{usd(balance)}</p>
                {pending > 0 && (
                  <p className="text-xs mt-1.5 text-amber-300" data-testid="wallet-pending">
                    + <strong>{usd(pending)}</strong> pending · releases 3 days after each event
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Lifetime earned: {usd(w.lifetime_earned_cents)} · Paid out: {usd(w.lifetime_paid_cents)}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                {!me.payouts_live ? (
                  <div className="max-w-[260px] text-right space-y-1" data-testid="payouts-locked-note">
                    <Badge variant="outline" className="border-amber-500/50 text-amber-300">Payouts temporarily paused</Badge>
                    <p className="text-[11px] text-muted-foreground">
                      Your balance is safe. Earnings unlock 3 days after each event and we&apos;ll email you the moment they&apos;re withdrawable.
                    </p>
                  </div>
                ) : !stripeReady ? (
                  <>
                    <Button onClick={onboardStripe} disabled={working} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="stripe-onboard-btn">
                      {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Connect with Stripe →</>}
                    </Button>
                    {w.stripe_account_id && (
                      <Button variant="outline" size="sm" onClick={refreshStripe} className="text-xs">I finished onboarding</Button>
                    )}
                    <p className="text-[11px] text-muted-foreground max-w-[220px] text-right">Required for instant payouts to your bank/debit card.</p>
                  </>
                ) : (
                  <>
                    <Button onClick={() => requestPayout(balance)} disabled={balance <= 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payout-btn">
                      <ArrowDownToLine className="h-4 w-4 mr-1.5" /> Cash out {usd(balance)}
                    </Button>
                    <p className="text-[11px] text-emerald-500 font-medium">Stripe connected ✓</p>
                    <p className="text-[11px] text-muted-foreground max-w-[220px] text-right">New earnings unlock 3 days after each event — we&apos;ll email you when they&apos;re ready.</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earnings statements */}
        {statements.length > 0 && (
          <Card className="border-border" data-testid="statements-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Earnings statements</h2>
                <Button size="sm" variant="outline" onClick={() => downloadStatement('all', 'all-events')} data-testid="statement-download-all">
                  <Download className="h-3.5 w-3.5 mr-1" /> Full statement (PDF)
                </Button>
              </div>
              <div className="space-y-2">
                {statements.map((s) => (
                  <div key={s.event_id} className="flex items-center justify-between gap-3 border border-border rounded-lg p-2.5" data-testid={`statement-row-${s.event_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.event?.name || s.event_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.count} sale{s.count === 1 ? '' : 's'} · earned ${((s.total_cents || 0) / 100).toFixed(2)}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadStatement(s.event_id, s.event?.name)} data-testid={`statement-download-${s.event_id}`}>
                      <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active campaigns */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold">Your campaigns</h2>
              <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                <button type="button" onClick={() => setCampTab('current')}
                  className={`px-3 py-1.5 font-semibold ${campTab === 'current' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:bg-muted'}`}
                  data-testid="campaigns-tab-current">
                  Current ({currentCampaigns.length})
                </button>
                <button type="button" onClick={() => setCampTab('past')}
                  className={`px-3 py-1.5 font-semibold ${campTab === 'past' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                  data-testid="campaigns-tab-past">
                  Past ({pastCampaigns.length})
                </button>
              </div>
            </div>
            {visibleCampaigns.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {campTab === 'past'
                  ? 'No past campaigns yet.'
                  : <>No active campaigns. <Link to="/promo-partners" className="text-indigo-400 hover:underline">Browse open campaigns →</Link></>}
              </p>
            ) : visibleCampaigns.map(c => (
              <div key={c.acceptance_id} className="border border-border rounded-lg p-3 space-y-2" data-testid={`campaign-${c.event_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-semibold text-sm">{c.event?.name}</p>
                    <p className="text-xs text-muted-foreground">{c.event?.city} · {new Date(c.event?.event_date).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-emerald-500 font-semibold"><DollarSign className="h-3 w-3 inline" />{((c.total_commission_cents || 0) / 100).toFixed(2)} earned</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5" data-testid={`campaign-kpis-${c.event_id}`}>
                  {[
                    ['Impr', c.kpis?.impressions ?? 0],
                    ['Clicks', c.kpis?.clicks ?? c.clicks ?? 0],
                    ['CTR', `${c.kpis?.ctr ?? 0}%`],
                    ['Sales', c.kpis?.sales ?? 0],
                    ['Tickets', c.kpis?.tickets ?? c.sales_count ?? 0],
                    ['Add-ons', c.kpis?.addons ?? 0],
                    ['Revenue', `$${((c.kpis?.revenue_cents ?? 0) / 100).toFixed(2)}`],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-muted/40 rounded px-1.5 py-1 text-center">
                      <p className="text-[9px] uppercase font-semibold text-muted-foreground">{label}</p>
                      <p className="text-xs font-bold">{val}</p>
                    </div>
                  ))}
                </div>
                {c.landing_url && (
                  <div className="flex items-center gap-2 bg-muted/40 rounded p-2">
                    <Input value={c.landing_url} readOnly className="text-xs font-mono h-8" data-testid={`tracking-url-${c.event_id}`} />
                    <Button size="sm" variant="outline" className="h-8" onClick={() => copy(c.landing_url, 'Tracking link')}><Copy className="h-3 w-3" /></Button>
                    <a href={c.landing_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="h-8"><ExternalLink className="h-3 w-3" /></Button></a>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setQrOpen(qrOpen === c.event_id ? null : c.event_id)} title="QR code" data-testid={`qr-toggle-${c.event_id}`}><QrCode className="h-3 w-3" /></Button>
                  </div>
                )}
                {qrOpen === c.event_id && c.landing_url && (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-border" data-testid={`qr-panel-${c.event_id}`}>
                    <div className="bg-white p-1.5 rounded">
                      <QRCodeCanvas id={`qr-canvas-${c.event_id}`} value={c.landing_url} size={140} level="M" includeMargin={false} />
                    </div>
                    <div className="flex-1 text-xs text-slate-700 space-y-2">
                      <p className="font-semibold text-slate-900">Print-ready QR</p>
                      <p className="text-slate-600 leading-relaxed">Stick it on flyers, stickers, table tents — every scan tracks back to your wallet.</p>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadQr(c.event_id, c.event?.name)} data-testid={`qr-download-${c.event_id}`}>
                        <Download className="h-3 w-3 mr-1" /> Download PNG
                      </Button>
                    </div>
                  </div>
                )}
                <PartnerLinkTools campaign={c} token={token} />
                {(c.marketing_images || []).length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground">Marketing Images <span className="normal-case text-muted-foreground/70">— tap to download for your socials/flyers</span></p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {c.marketing_images.map((u, i) => (
                        <div key={i} className="relative group aspect-square rounded overflow-hidden border border-border bg-muted/40" data-testid={`marketing-image-${c.event_id}-${i}`}>
                          <img src={u} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => downloadImage(u, c.event?.name, i)}
                            className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/55 transition opacity-0 hover:opacity-100 focus:opacity-100"
                            title="Download"
                            data-testid={`marketing-image-download-${c.event_id}-${i}`}
                          >
                            <span className="flex items-center gap-1 text-white text-[11px] font-semibold bg-emerald-600/90 px-2 py-1 rounded">
                              <Download className="h-3 w-3" /> Save
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {c.marketing_directive && (
                  <div className="text-xs">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground">Directive</p>
                    <p>{c.marketing_directive}</p>
                  </div>
                )}
                {c.marketing_copy && (
                  <div className="text-xs">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase font-semibold text-muted-foreground">Copy</p>
                      <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => copy(c.marketing_copy.replace('{LINK}', c.landing_url), 'Copy')}><Copy className="h-3 w-3 mr-1" />Copy</Button>
                    </div>
                    <p className="font-mono whitespace-pre-wrap text-muted-foreground bg-muted/30 p-2 rounded">{c.marketing_copy.replace('{LINK}', c.landing_url)}</p>
                  </div>
                )}
                {c.landing_url && <ShareKitPanel campaign={c} token={token} />}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-2">
            <h2 className="font-semibold">Wallet activity</h2>
            {txs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3 text-center">No activity yet.</p>
            ) : txs.map(t => (
              <div key={t.tx_id} className="flex items-center justify-between text-xs border-b border-border py-1.5" data-testid={`tx-${t.tx_id}`}>
                <div>
                  <p className="font-medium capitalize">{t.type}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
                </div>
                <p className={`font-mono font-semibold ${t.amount_cents > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {t.amount_cents > 0 ? '+' : ''}{usd(Math.abs(t.amount_cents))}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
